import { useCallback, useEffect, useState } from 'react';
import { LoaderCircle, BarChart3, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { api, eur, num } from '../../lib/project';
import { monthsToShort } from '../../lib/api';
import { AdminEditorBody } from '../acquisition/AdminEditor';
import '../../acquisition.css';

// Compact side-by-side comparison of every scenario: units, revenue, EBITDA
// per year (1/2/3) plus payback and 3-year cash. Reads the deterministic
// annual engine (/acquisition/all).
function ScenarioComparison({ scenarios }) {
  const keys = Object.keys(scenarios || {});
  if (!keys.length) return null;
  const yrs = (sc) => sc.years || [];
  const triple = (sc, pick, fmt) => yrs(sc).map((y, i) => (
    <span key={i} className="cmp-triple-item">{fmt(pick(y))}</span>
  ));
  return (
    <div className="pressupostos-compare" data-testid="scenario-comparison">
      <div className="pressupostos-compare-head">
        <BarChart3 size={16} />
        <div>
          <strong>Comparação de cenários</strong>
          <small>Receita, EBITDA e payback lado a lado · anos 1 · 2 · 3 (motor anual determinístico)</small>
        </div>
      </div>
      <div className="pressupostos-compare-scroll">
        <table className="cmp-table" data-testid="scenario-comparison-table">
          <thead>
            <tr>
              <th>Cenário</th>
              <th>Unidades <em>1·2·3</em></th>
              <th>Receita s/ IVA <em>1·2·3</em></th>
              <th>EBITDA <em>1·2·3</em></th>
              <th>Payback</th>
              <th>Caixa 3 anos</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => {
              const sc = scenarios[key];
              const payback = sc.payback_total_capital_months;
              return (
                <tr key={key} data-testid={`scenario-comparison-row-${key}`}>
                  <th scope="row">
                    <span className="cmp-dot" style={{ background: sc.color || '#334155' }} aria-hidden="true" />
                    {sc.label || key}
                  </th>
                  <td><div className="cmp-triple">{triple(sc, (y) => y.units, (v) => num(v))}</div></td>
                  <td><div className="cmp-triple">{triple(sc, (y) => y.revenue_net, (v) => eur(v, 0))}</div></td>
                  <td><div className="cmp-triple">{triple(sc, (y) => y.ebitda, (v) => eur(v, 0))}</div></td>
                  <td className="cmp-strong">{monthsToShort(payback)}</td>
                  <td className="cmp-strong">{eur(sc.total_operating_cash_3y, 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PressupostosTab() {
  const [config, setConfig] = useState(null);
  const [computed, setComputed] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [{ data: cfg }, { data: all }] = await Promise.all([
        api.get('/acquisition/config'),
        api.get('/acquisition/all'),
      ]);
      setConfig(cfg);
      setComputed(all);
      setError('');
    } catch {
      setError('Não foi possível carregar os pressupostos e cenários.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [restoring, setRestoring] = useState(false);
  const restoreDefaults = useCallback(async () => {
    if (restoring) return;
    if (!window.confirm('Restaurar os dados iniciais? As suas edições atuais (preços, custos, cenários) serão substituídas pelos valores originais.')) return;
    setRestoring(true);
    try {
      await api.post('/acquisition/restore-defaults');
      window.dispatchEvent(new Event('lio-config-changed'));
      await load();
      toast.success('Dados iniciais restaurados.');
    } catch {
      toast.error('Não foi possível restaurar os dados iniciais.');
    } finally {
      setRestoring(false);
    }
  }, [restoring, load]);

  return (
    <div className="pressupostos-tab" data-testid="pressupostos-tab">
      <div className="plan-group-heading pressupostos-heading">
        <div>
          <h3>Pressupostos &amp; cenários</h3>
          <p>As variáveis do dossier de aquisição — investimento, fiscalidade, custos, produtos e vendas anuais por cenário — vivem aqui. Guardar recalcula os cenários, a sensibilidade e todos os indicadores. As alterações ficam guardadas automaticamente como o estado atual do plano.</p>
        </div>
        <button type="button" className="pressupostos-restore-btn" data-testid="restore-defaults-button" onClick={restoreDefaults} disabled={restoring || busy}>
          {restoring ? <LoaderCircle size={14} className="spin" /> : <RotateCcw size={14} />}
          Restaurar dados iniciais
        </button>
      </div>

      {busy && <p className="pressupostos-loading" data-testid="pressupostos-loading"><LoaderCircle size={16} className="spin" /> A carregar pressupostos…</p>}
      {error && <p role="alert" className="editor-validation-error" data-testid="pressupostos-error">{error}</p>}

      {!busy && computed && <ScenarioComparison scenarios={computed.scenarios} />}
      {!busy && config && (
        <AdminEditorBody config={config} onSaved={load} onClose={undefined} />
      )}
    </div>
  );
}
